import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { baseApi } from '../services';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import { unauthorizedMiddleware } from './middleware/unauthorized-middleware';
import projectsSlices from './slices/projectsSlices';
import companySlice from './slices/companySlice';
import configurationSlice from './slices/configurationSlice';
import integrationSlices from './slices/integrationSlice';
import menuSlice from './slices/menuSlice';
import playgroundSlices from './slices/playgroundSlices';
import knowledgeBaseSlices from './slices/knowledgeBaseSlices';
import creationFlowSlices from './slices/creationFlowSlices';
import countDownSlice from './slices/countDownSlice';
import analyticSlices from './slices/analyticSlices';
import limitBannerSlice from './slices/limitBannerSlice';
import planSlice from 'src/store/slices/planSlice';
import onboardingSlice from './slices/onboardingSlice';
import registerSlice from 'src/store/slices/registerSlice';
import storage from 'redux-persist/lib/storage';
import { persistReducer, persistStore } from 'redux-persist';
import { baseSsoApi } from 'src/services/base.sso.api';
import { errorNotificationMiddleware } from 'src/store/middleware/error-notification-middleware';

const persistConfig = {
  key: 'company',
  storage,
};

const persistConfigIntegration = {
  key: 'integration',
  storage,
};

const persistConfigMenu = {
  key: 'menu',
  storage,
};
const persistConfigLimitBanner = {
  key: 'limitBanner',
  storage,
};
const persistConfigPlayground = {
  key: 'playground',
  storage,
};
const persistConfigAnalytics = {
  key: 'analytics',
  storage,
};
const persistConfigCreationFlow = {
  key: 'creation-flow',
  storage,
};
const persistOnboarding = {
  key: 'onboarding',
  storage,
};
const persistCountDown = {
  key: 'countdown',
  storage,
};
const persistRegister = {
  key: 'register',
  storage,
};
const persistConfiguration = {
  key: 'configuration',
  storage,
};

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  [baseSsoApi.reducerPath]: baseSsoApi.reducer,
  projectsSlices,
  knowledgeBaseSlices,
  planSlice,
  analyticSlices: persistReducer(persistConfigAnalytics, analyticSlices),
  creationFlowSlices: persistReducer(persistConfigCreationFlow, creationFlowSlices),
  playgroundSlices: persistReducer(persistConfigPlayground, playgroundSlices),
  companySlices: persistReducer(persistConfig, companySlice),
  integrationSlices: persistReducer(persistConfigIntegration, integrationSlices),
  menuSlices: persistReducer(persistConfigMenu, menuSlice),
  limitBannerSlices: persistReducer(persistConfigLimitBanner, limitBannerSlice),
  onboardingSlice: persistReducer(persistOnboarding, onboardingSlice),
  countDownSlice: persistReducer(persistCountDown, countDownSlice),
  registerSlice: persistReducer(persistRegister, registerSlice),
  configurationSlice: persistReducer(persistConfiguration, configurationSlice),
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat([
      unauthorizedMiddleware,
      errorNotificationMiddleware,
      baseApi.middleware,
      baseSsoApi.middleware,
    ]),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
